import { state } from './state';

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

export interface SpeechRecognitionLike {
  lang: string;
  interimResults: boolean;
  maxAlternatives: number;
  continuous: boolean;
  onresult: ((event: { results: Array<Array<{ transcript: string }>> }) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  abort(): void;
}

let active: SpeechRecognitionLike | null = null;

export function recognitionCtor(): SpeechRecognitionCtor | null {
  const w = window as unknown as Record<string, SpeechRecognitionCtor | undefined>;
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export function stopRecognition(): void {
  if (!active) return;
  try { active.abort(); } catch { /* ya detenido */ }
  active = null;
}

export interface ListenOptions {
  locale: string;
  onResult: (transcript: string) => void;
  onError: (kind: 'denied' | 'unavailable') => void;
  onEnd: () => void;
}

/** Devuelve false si el navegador no soporta reconocimiento de voz. */
export function listen(options: ListenOptions): boolean {
  const Ctor = recognitionCtor();
  if (!Ctor) return false;
  stopRecognition();

  const rec = new Ctor();
  active = rec;
  rec.lang = options.locale;
  rec.interimResults = false;
  rec.maxAlternatives = 1;
  rec.continuous = false;

  rec.onresult = (event) => {
    const transcript = event.results?.[0]?.[0]?.transcript ?? '';
    if (transcript) options.onResult(transcript);
  };
  rec.onerror = (event) => {
    options.onError(
      event.error === 'not-allowed' || event.error === 'service-not-allowed' ? 'denied' : 'unavailable',
    );
  };
  rec.onend = () => {
    if (active === rec) active = null;
    options.onEnd();
  };

  try {
    rec.start();
    return true;
  } catch {
    stopRecognition();
    return false;
  }
}

export function speak(text: string, locale: string, force = false): void {
  if (!('speechSynthesis' in window) || !text || (!state.autoVoice && !force)) return;
  try {
    speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = locale;
    utterance.rate = 0.96;
    utterance.volume = state.voiceVolume;
    speechSynthesis.speak(utterance);
  } catch { /* sintesis no disponible */ }
}
